import { useMemo, useState } from "react";
import { useSeo, SITE_ORIGIN, SITE_NAME } from "@/hooks/useSeo";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import Reveal from "@/components/feature/Reveal";
import ZoneCard from "@/components/feature/ZoneCard";
import { zones, zoneCategories } from "@/mocks/zones";

export default function ZonesPage() {
  const [category, setCategory] = useState("全部");
  const [query, setQuery] = useState("");

  useSeo({
    title: "专区总览 - 爆料91栏目频道板块合集导航",
    description:
      "爆料91专区总览页收录全部栏目、频道与板块入口，按影视、综艺、音乐、网络、榜单分类，支持关键词筛选，一个入口看齐爆料91全部内容合集。",
    keywords: "爆料91专区,爆料91栏目,爆料91频道,爆料91板块,爆料91内容合集,专区导航",
    path: "/zones",
    type: "website",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: "爆料91专区总览",
        url: `${SITE_ORIGIN}/zones`,
        inLanguage: "zh-CN",
        description: "按分类汇总爆料91全部专区、栏目与板块入口。",
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: `${SITE_ORIGIN}/` },
          { "@type": "ListItem", position: 2, name: "专区总览", item: `${SITE_ORIGIN}/zones` },
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "ItemList",
        name: "爆料91专区列表",
        itemListElement: zones.map((zone, index) => ({
          "@type": "ListItem",
          position: index + 1,
          name: zone.name,
          url: `${SITE_ORIGIN}/zone/${zone.id}`,
        })),
      },
    ],
  });

  const filtered = useMemo(() => {
    const keyword = query.trim().toLowerCase();
    return zones.filter((zone) => {
      const matchCategory = category === "全部" || zone.category === category;
      const matchKeyword =
        !keyword ||
        zone.name.toLowerCase().includes(keyword) ||
        zone.subtitle.toLowerCase().includes(keyword) ||
        zone.tags.some((tag) => tag.toLowerCase().includes(keyword));
      return matchCategory && matchKeyword;
    });
  }, [category, query]);

  const totalCount = zones.reduce((sum, zone) => sum + zone.count, 0);

  return (
    <>
      <section className="w-full bg-background-100 border-b border-background-200">
        <div className="w-full px-4 md:px-8 pt-6 pb-10 md:pb-14">
          <Breadcrumbs items={[{ label: "首页", to: "/" }, { label: "专区总览" }]} />
          <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
            <div className="lg:col-span-8">
              <span className="inline-flex items-center gap-2 text-xs font-label tracking-[0.2em] uppercase text-primary-700">
                <span className="w-5 h-px bg-primary-500"></span>
                Zones
              </span>
              <h1 className="mt-3 font-heading font-black text-3xl md:text-4xl text-foreground-950">
                爆料91专区总览
              </h1>
              <p className="mt-4 max-w-2xl text-sm md:text-base leading-relaxed text-foreground-600">
                这里收录全部栏目、频道与板块入口。先按分类收窄范围，再用关键词找具体方向，
                点进去就是该专区下的全部内容合集，不用在多个页面之间来回跳。
              </p>
            </div>
            <div className="lg:col-span-4">
              <dl className="grid grid-cols-2 gap-4">
                <div className="rounded-lg border border-background-200 bg-background-50 p-4">
                  <dd className="font-heading font-bold text-2xl text-foreground-950">{zones.length}</dd>
                  <dt className="mt-1 text-xs text-foreground-600">收录专区</dt>
                </div>
                <div className="rounded-lg border border-background-200 bg-background-50 p-4">
                  <dd className="font-heading font-bold text-2xl text-foreground-950">{totalCount}</dd>
                  <dt className="mt-1 text-xs text-foreground-600">在架合集</dt>
                </div>
              </dl>
            </div>
          </div>
        </div>
      </section>

      <section className="w-full bg-background-50">
        <div className="w-full px-4 md:px-8 py-8 md:py-12">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div className="flex flex-wrap items-center gap-1 p-1 rounded-full border border-background-300 bg-background-100 w-fit">
              {zoneCategories.map((item) => (
                <button
                  key={item}
                  type="button"
                  onClick={() => setCategory(item)}
                  className={`px-4 py-2 rounded-full text-sm whitespace-nowrap cursor-pointer transition-colors ${
                    category === item
                      ? "bg-primary-600 text-background-50"
                      : "text-foreground-700 hover:text-foreground-950"
                  }`}
                >
                  {item}
                </button>
              ))}
            </div>

            <label className="relative flex items-center w-full lg:w-72">
              <i className="ri-search-line absolute left-3 text-foreground-500 text-base"></i>
              <input
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="搜索专区名称或标签"
                className="w-full h-11 pl-10 pr-4 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-300"
              />
            </label>
          </div>

          <p className="mt-6 text-sm text-foreground-500">
            共找到 <span className="text-foreground-900 font-medium">{filtered.length}</span> 个专区
            {category !== "全部" && <span> · 分类：{category}</span>}
          </p>

          {filtered.length > 0 ? (
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {filtered.map((zone, index) => (
                <Reveal key={zone.id} delay={index * 60}>
                  <ZoneCard zone={zone} />
                </Reveal>
              ))}
            </div>
          ) : (
            <div className="mt-10 rounded-lg border border-dashed border-background-300 bg-background-100 py-16 flex flex-col items-center text-center">
              <i className="ri-search-eye-line text-3xl text-foreground-400"></i>
              <p className="mt-4 font-heading font-semibold text-lg text-foreground-900">
                没有匹配的专区
              </p>
              <p className="mt-2 text-sm text-foreground-500">
                换个关键词，或把分类切回「全部」再试一次。
              </p>
              <button
                type="button"
                onClick={() => {
                  setQuery("");
                  setCategory("全部");
                }}
                className="mt-5 h-10 px-5 rounded-md bg-primary-600 text-background-50 text-sm whitespace-nowrap cursor-pointer hover:bg-primary-700 transition-colors"
              >
                重置筛选条件
              </button>
            </div>
          )}
        </div>
      </section>
    </>
  );
}